// TailLookupSheet — bottom sheet for tail-number (N-number) flight history.
//
// Opens when the user types a US N-number into the SearchTypeahead and
// submits. Shows the aircraft's last N flights (up to 20) from FlightAware
// AeroAPI, using the optimistic IDB cache pattern from aeroapiCache.ts.
//
// Tapping a flight row fetches its position track + filed route fixes, then
// notifies the parent via onShowTrack so MapPage can render the track layer.
//
// Offline behaviour:
//   - Tail lookup: renders cached data instantly with a "last refreshed HH:MM"
//     badge. 5 s AbortController ceiling; navigator.onLine fast-path skips
//     network entirely when offline.
//   - Flight track: cached per fa_flight_id; shows cached track instantly.
//   - If no cache AND offline: shows a friendly "Cannot look up tail offline"
//     message.

import { useEffect, useRef, useState, useCallback } from 'react';
import { Plane, Clock, X, EyeOff, AlertCircle, Loader2 } from '../shell/Icon';
import { aeroapiGet, aeroapiSet } from '../lib/aeroapiCache.js';
import { formatLocalTimeBare } from '../lib/timeFormat.js';
import { FlightRow, formatFlightDate, formatFlightTime, truncateRoute } from '../aircraft/FlightRow.js';
import type { TailLookupResponse, TailFlight } from '../../api/flightaware/tail-lookup.js';
import type { FlightTrackResponse } from '../../api/flightaware/flight-track.js';

// TTL constants
const TTL_TAIL_FRESH = 24 * 60 * 60 * 1000;  // 1 day fresh for UI
// tracks: indefinite (historical, don't change)

interface Props {
  /** The N-number being looked up (e.g. "N629RA"). */
  ident: string;
  /** Called when the user wants to dismiss. */
  onClose: () => void;
  /** Called with the flight track to render on the map. Null = clear. */
  onShowTrack: (track: FlightTrackResponse | null) => void;
  /** The fa_flight_id currently shown on the map (if any). */
  activeTrackId: string | null;
}

// ── helpers ────────────────────────────────────────────────────────────────

// formatDate/formatTime/truncateRoute are now in src/aircraft/FlightRow.tsx
// Re-export aliases for use within this file.
const formatDate = formatFlightDate;
const formatTime = formatFlightTime;

function refreshLabel(cachedAt: number | null): string {
  if (!cachedAt) return '';
  const d = new Date(cachedAt);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - d.getTime()) / 86_400_000);
  const hhmm = formatLocalTimeBare(d);
  if (diffDays === 0) return hhmm;
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return `${days[d.getDay()]} ${hhmm}`;
}

// ── main component ─────────────────────────────────────────────────────────

export default function TailLookupSheet({ ident, onClose, onShowTrack, activeTrackId }: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flights, setFlights] = useState<TailFlight[] | null>(null);
  const [cachedAt, setCachedAt] = useState<number | null>(null);
  const [fromCache, setFromCache] = useState(false);

  // per-flight track loading state
  const [loadingTrackId, setLoadingTrackId] = useState<string | null>(null);
  const [trackError, setTrackError] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const trackAbortRef = useRef<AbortController | null>(null);

  // ── fetch tail lookup ──────────────────────────────────────────────────

  const doFetch = useCallback(async () => {
    abortRef.current?.abort();
    setLoading(true);
    setError(null);

    const cacheKey = `tail|${ident}`;

    // Step 1: render from IDB cache immediately.
    const cached = await aeroapiGet<TailLookupResponse>(cacheKey);
    if (cached) {
      setFlights(cached.data.flights);
      setCachedAt(cached.cachedAt);
      setFromCache(true);
    }

    // Step 2: fast-path — skip network if offline.
    if (!navigator.onLine) {
      setLoading(false);
      if (!cached) {
        setError('Cannot look up tail offline. Try again with internet.');
      }
      return;
    }

    // Step 3: network with 5 s AbortController ceiling.
    const ac = new AbortController();
    abortRef.current = ac;
    const timer = setTimeout(() => ac.abort(), 5000);

    try {
      const r = await fetch(
        `/api/flightaware/tail-lookup?ident=${encodeURIComponent(ident)}&limit=20`,
        { signal: ac.signal },
      );
      clearTimeout(timer);

      if (r.status === 503) {
        // AeroAPI not configured — silently keep cached data if present.
        setLoading(false);
        if (!cached) setError('Tail lookup requires FlightAware AeroAPI configuration.');
        return;
      }
      if (r.status === 404) {
        setLoading(false);
        setError(`Aircraft ${ident} not found in FlightAware. Check the registration.`);
        setFlights(null);
        return;
      }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);

      const data = (await r.json()) as TailLookupResponse;
      await aeroapiSet(cacheKey, data);
      setFlights(data.flights);
      setCachedAt(Date.now());
      setFromCache(false);
      setLoading(false);
    } catch (e: unknown) {
      clearTimeout(timer);
      if (ac.signal.aborted) {
        // Timed out — keep cached data, mark as stale.
        setLoading(false);
        if (!cached) setError('Request timed out. Check your connection.');
      } else {
        setLoading(false);
        if (!cached) setError((e as Error)?.message ?? 'Unknown error');
      }
    }
  }, [ident]);

  useEffect(() => {
    doFetch();
    return () => {
      abortRef.current?.abort();
      trackAbortRef.current?.abort();
    };
  }, [doFetch]);

  // ── fetch flight track ─────────────────────────────────────────────────

  const handleSelectFlight = useCallback(async (flight: TailFlight) => {
    const flightId = flight.faFlightId;

    // Toggle: if already showing this track, hide it.
    if (activeTrackId === flightId) {
      onShowTrack(null);
      return;
    }

    setTrackError(null);
    setLoadingTrackId(flightId);
    trackAbortRef.current?.abort();

    const cacheKey = `track|${flightId}`;

    // Step 1: optimistic render from cache.
    const cached = await aeroapiGet<FlightTrackResponse>(cacheKey);
    if (cached) {
      onShowTrack(cached.data);
      setLoadingTrackId(null);
    }

    if (!navigator.onLine) {
      if (!cached) {
        setTrackError('Cannot load flight track offline.');
        setLoadingTrackId(null);
      }
      return;
    }

    // Step 2: network fetch with 5 s ceiling.
    const ac = new AbortController();
    trackAbortRef.current = ac;
    const timer = setTimeout(() => ac.abort(), 5000);

    try {
      const r = await fetch(
        `/api/flightaware/flight-track?id=${encodeURIComponent(flightId)}`,
        { signal: ac.signal },
      );
      clearTimeout(timer);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = (await r.json()) as FlightTrackResponse;
      await aeroapiSet(cacheKey, data);
      onShowTrack(data);
      setLoadingTrackId(null);
    } catch (e: unknown) {
      clearTimeout(timer);
      if (!cached) {
        setTrackError((e as Error)?.message ?? 'Failed to load track');
      }
      setLoadingTrackId(null);
    }
  }, [activeTrackId, onShowTrack]);

  // ── render ─────────────────────────────────────────────────────────────

  return (
    <div
      className="absolute inset-0 z-40"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="absolute bottom-0 left-0 right-0 bg-cockpit-900/97 backdrop-blur border-t border-cockpit-600 rounded-t-2xl shadow-2xl flex flex-col"
        style={{
          maxHeight: '75vh',
          paddingBottom: 'calc(env(safe-area-inset-bottom) + 0.75rem)',
        }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={`Tail lookup: ${ident}`}
      >
        {/* Handle bar */}
        <div className="flex justify-center pt-2.5 pb-1 flex-shrink-0">
          <span className="w-10 h-1 bg-cockpit-600 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 pb-2 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Plane size={16} className="text-sky-400" />
            <h3 className="font-semibold text-cockpit-100 text-sm tracking-wide">{ident}</h3>
            {loading && <Loader2 size={12} className="text-cockpit-400 animate-spin" />}
          </div>
          <div className="flex items-center gap-3">
            {activeTrackId && (
              <button
                onClick={() => onShowTrack(null)}
                className="flex items-center gap-1 text-[11px] text-cockpit-300 hover:text-cockpit-100"
                title="Hide track"
              >
                <EyeOff size={12} />
                <span>Hide track</span>
              </button>
            )}
            <button
              onClick={onClose}
              className="text-cockpit-400 hover:text-cockpit-200"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Staleness badge */}
        {cachedAt && (fromCache || !loading) && (
          <div className="px-4 pb-2 flex-shrink-0">
            <div className="flex items-center gap-1.5 text-[10px] text-cockpit-400">
              <Clock size={9} />
              <span>Last refreshed {refreshLabel(cachedAt)}</span>
            </div>
          </div>
        )}

        {/* Track error banner */}
        {trackError && (
          <div className="mx-4 mb-2 px-3 py-2 bg-red-900/40 border border-red-700/50 rounded-lg flex items-center gap-2 flex-shrink-0">
            <AlertCircle size={12} className="text-red-400 flex-shrink-0" />
            <span className="text-[11px] text-red-200">{trackError}</span>
            <button
              className="ml-auto text-red-400 hover:text-red-200"
              onClick={() => setTrackError(null)}
            >
              <X size={12} />
            </button>
          </div>
        )}

        {/* Content */}
        <div className="overflow-y-auto flex-1 min-h-0">
          {/* Error state */}
          {error && !flights && (
            <div className="px-4 py-8 text-center">
              <AlertCircle size={24} className="text-cockpit-500 mx-auto mb-2" />
              <p className="text-sm text-cockpit-300">{error}</p>
              {navigator.onLine && (
                <button
                  onClick={doFetch}
                  className="mt-3 text-xs text-sky-400 hover:text-sky-300"
                >
                  Retry
                </button>
              )}
            </div>
          )}

          {/* Loading with no cache */}
          {loading && !flights && !error && (
            <div className="px-4 py-8 text-center">
              <Loader2 size={24} className="text-cockpit-500 mx-auto mb-2 animate-spin" />
              <p className="text-sm text-cockpit-400">Looking up {ident}…</p>
            </div>
          )}

          {/* Flight list */}
          {flights && flights.length === 0 && (
            <div className="px-4 py-8 text-center">
              <Plane size={24} className="text-cockpit-600 mx-auto mb-2" />
              <p className="text-sm text-cockpit-400">No recent flights found for {ident}</p>
            </div>
          )}

          {flights && flights.length > 0 && (
            <div>
              <div className="px-4 py-2 text-[10px] tracking-widest text-cockpit-400">
                RECENT FLIGHTS · {flights.length}
              </div>
              {flights.map((flight) => (
                <FlightRow
                  key={flight.faFlightId}
                  flight={flight}
                  isSelected={activeTrackId === flight.faFlightId}
                  isLoadingTrack={loadingTrackId === flight.faFlightId}
                  onSelect={() => handleSelectFlight(flight)}
                />
              ))}
              {/* Info footer */}
              <div className="px-4 py-3 text-[10px] text-cockpit-500 text-center">
                Tap a flight to show its track on the map
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
